import { GAME_WIDTH, GAME_HEIGHT } from '../config.js';
import { ySortDepth } from '../ui/ySort.js';
import { ALTANA_BY_LEVEL, BACKYARD_OBJECT_FOREGROUND_BY_LEVEL } from './backyardLayout.js';
import { STREET_FLOWER_POLYS } from './streetForeground.mjs';

// Foreground occlusion: certain polygons of a scene's background are re-drawn ABOVE the
// placed objects (animals / budy / Natalka), so things standing "behind" the building get
// covered by it. No new art — we mask a copy of the scene's own background.
//
// Each entry is an array of polygons; each polygon is a flat [x0,y0,x1,y1,...] in world
// coords (authored via SceneEditMode — press E in dev). Keys are backend scene names.
export const FOREGROUND_BY_SCENE = {
  // Game Hall — downstairs. Authored 2026-07-08 (Task 5) by tracing bg_game_hall_down.webp
  // (1280×720, pixel==world coords). Each polygon's LOWEST point is its ground/sort line;
  // it sits within the matching DOWN_OBSTACLES rect so avatars behind an object are hidden
  // and avatars standing in front (feet below the ground line) draw over it.
  game_hall_down: [
    [283, 333, 561, 336, 561, 430, 283, 440],                                        // lada (counter) front
    // stolik t1 (square, front-left) — full table+chairs cutout so an avatar in the corridor
    // behind it (feet y450–520) is masked ("walks behind the table"). Top edge hugs the chair
    // backs / tabletop back edge; ground line 719 (the table's front is cut off by the screen
    // bottom, so every reachable overlap is "behind").
    // Sides clamped 2026-07-09 to the obstacle's x-range (265–615) so the corridor between
    // t1 and t2 is never masked.
    [305, 480, 385, 480, 390, 545, 525, 550, 530, 487, 605, 490, 612, 548, 615, 570, 615, 719, 268, 719, 265, 592, 265, 566, 300, 545],
    // t2/t3/kanapa fronts clamped 2026-07-09 to the hand-tuned DOWN_OBSTACLES bounds —
    // a cutout must never extend past its obstacle (a reachable feet position under a
    // mask renders the avatar "sliced" by re-drawn background).
    [680, 440, 700, 414, 815, 413, 880, 438, 865, 480, 840, 555, 690, 555, 680, 480], // stolik t2 (round, centre) + front chairs
    [955, 425, 1010, 396, 1070, 393, 1120, 425, 1105, 470, 1085, 500, 990, 500, 962, 470], // stolik t3 (round, right) + front chairs
    [798, 340, 1015, 340, 1015, 405, 800, 405],                                      // kanapa (sofa) front
    // schody (near stair base + newel posts) — bottom capped at y400, the stair base's visual
    // ground line. The strip below (feet y400–460) is walkable: y400–430 is DOWN_STAIRS_ZONE,
    // y430–460 the pass-by lane behind t2 — neither may ever be masked.
    [628, 368, 700, 360, 752, 372, 752, 400, 700, 400, 628, 400],
  ],
  // Game Hall — upstairs. Authored 2026-07-08 (Task 5) from bg_game_hall_up.webp. The
  // balustrade recedes toward the back-right, so it is split into two segments each sorted
  // at its own local base to avoid occluding avatars standing in front of the far end.
  game_hall_up: [
    [178, 300, 300, 285, 345, 250, 345, 325, 180, 333],                              // balustrada (railing) — near/left segment
    [345, 250, 450, 258, 560, 238, 562, 300, 345, 322],                              // balustrada (railing) — far/right segment
    // t4–t7 cutouts clamped 2026-07-09 to the hand-tuned UP_OBSTACLES bounds — a cutout
    // must never extend past its obstacle (a reachable feet position under a mask renders
    // the avatar "sliced" by re-drawn background).
    [118, 414, 185, 386, 300, 386, 333, 414, 333, 480, 330, 528, 155, 528, 128, 470], // stolik t4 (round, left) + front chairs
    // stolik t5 (square, front-centre) — full table+chairs cutout (chairs included) so an
    // avatar in the lane behind it is masked. Ground line 683 = just above the obstacle's
    // bottom edge (685); the strip below stays in front.
    [445, 540, 445, 470, 470, 428, 530, 422, 555, 407, 655, 410, 710, 425, 710, 508, 700, 590, 655, 683, 480, 683, 445, 590],
    [690, 320, 700, 305, 800, 300, 835, 322, 815, 360, 800, 405, 690, 405, 690, 360], // stolik t6 (rect, centre) + front chairs
    [1049, 360, 1090, 338, 1190, 338, 1213, 358, 1213, 400, 1200, 433, 1090, 433, 1055, 400], // stolik t7 (rect, right) + front chairs
  ],
  // Podwórko altana bench (per level — the gazebo moves between levels). Keys match
  // altanaForegroundKey(level) in backyardLayout.js.
  // …plus the yard objects you can walk behind (barrel, logs, sapling — see backyardLayout).
  backyard_lvl1: [...BACKYARD_OBJECT_FOREGROUND_BY_LEVEL[1]],
  backyard_lvl2: [...BACKYARD_OBJECT_FOREGROUND_BY_LEVEL[2]],
  backyard_lvl3: [ALTANA_BY_LEVEL[3].occluder, ...BACKYARD_OBJECT_FOREGROUND_BY_LEVEL[3]],
  backyard_lvl4: [ALTANA_BY_LEVEL[4].occluder, ...BACKYARD_OBJECT_FOREGROUND_BY_LEVEL[4]],
  backyard_lvl5: [ALTANA_BY_LEVEL[5].occluder, ...BACKYARD_OBJECT_FOREGROUND_BY_LEVEL[5]],
  // The street's flower beds (world coords of the scrolled panorama — see streetForeground.mjs).
  street: STREET_FLOWER_POLYS,
};

function polyPoints(flat) {
  const pts = [];
  for (let i = 0; i < flat.length; i += 2) pts.push({ x: flat[i], y: flat[i + 1] });
  return pts;
}

// Draw the foreground for `backendScene` (e.g. 'game_hall_down') using `bgKey` as the current
// background texture. Each polygon becomes its OWN masked layer depth-sorted by its base
// (bottom-most y) via ySortDepth, so a walking avatar/buda is occluded when it stands
// behind an object and draws in front when below it. Returns { setTexture, destroy } or
// null if the scene has no foreground regions. Call setTexture on day/night phase changes.
export function applyForeground(scene, backendScene, bgKey, placement = {}) {
  const polys = FOREGROUND_BY_SCENE[backendScene];
  if (!polys || polys.length === 0) return null;
  // Where the scene draws its background: a 1280×720 frame at the canvas centre by default.
  // The street passes its tiles ({key, x, y, w, h, originX, originY} each) so every masked
  // copy is placed exactly like the tile it copies; a polygon spanning two tiles gets one
  // layer per tile it overlaps.
  const { x = GAME_WIDTH / 2, y = GAME_HEIGHT / 2, w = GAME_WIDTH, h = GAME_HEIGHT } = placement;
  const sources = placement.tiles ?? [{ key: bgKey, x, y, w, h, originX: 0.5, originY: 0.5 }];
  const spanX = (src) => [src.x - src.w * src.originX, src.x + src.w * (1 - src.originX)];

  const layers = polys.flatMap((flat) => {
    const pts = polyPoints(flat);
    if (pts.length < 3) return [];
    const minX = Math.min(...pts.map((p) => p.x)), maxX = Math.max(...pts.map((p) => p.x));
    const baseY = Math.max(...pts.map((p) => p.y));   // object's ground line → sort anchor
    return sources.filter((src) => { const [a, b] = spanX(src); return b > minX && a < maxX; }).map((src) => {
      const maskG = scene.make.graphics({ x: 0, y: 0, add: false });
      maskG.fillStyle(0xffffff);
      maskG.fillPoints(pts, true);
      const fg = scene.add.image(src.x, src.y, src.key)
        .setOrigin(src.originX, src.originY).setDisplaySize(src.w, src.h).setDepth(ySortDepth(baseY));
      fg.setMask(maskG.createGeometryMask());
      return { fg, maskG };
    });
  });

  return {
    setTexture: (key) => {
      // Phase swaps only apply to a single-texture background (tiled scenes have no phases).
      if (key && !placement.tiles && scene.textures.exists(key)) for (const l of layers) l.fg.setTexture(key).setDisplaySize(w, h);
    },
    // The street crossfades its looks (street/streetLook.js): the incoming look's copies fade in with its tiles.
    setAlpha: (a) => { for (const l of layers) l.fg.setAlpha(a); },
    destroy: () => {
      for (const l of layers) { l.fg.clearMask(true); l.fg.destroy(); l.maskG.destroy(); }
    },
  };
}
